import {
  IconArrowsRight,
  IconCalendar,
  IconChecklist,
  IconClipboard,
  IconId,
  IconMail,
  IconPackage,
  IconQuestionMark,
  IconSchool,
} from '@tabler/icons-react';
import type {
  WorkflowTriggerResponse} from 'src/api/ProactApiClient';
import {
  NexusNodeType,
  WorkflowActionStateEnum,
  WorkflowDefinitionActionTypeEnum,
  WorkflowDefinitionTriggerTypeEnum,
  WorkflowStateEnum
} from 'src/api/ProactApiClient';

// --- Entity type config ---

export interface WorkflowEntityConfig {
  icon: React.FC<{size?: number | string}>;
  color: string;
  label: string;
}

export const workflowEntityConfig: Record<NexusNodeType, WorkflowEntityConfig> = {
  [NexusNodeType.Workflow]: {icon: IconArrowsRight, color: 'blue', label: 'Workflow'},
  [NexusNodeType.Task]: {icon: IconChecklist, color: 'orange', label: 'Task'},
  [NexusNodeType.Form]: {icon: IconClipboard, color: 'green', label: 'Form'},
  [NexusNodeType.Email]: {icon: IconMail, color: 'violet', label: 'Email'},
  [NexusNodeType.TrainingSession]: {icon: IconSchool, color: 'cyan', label: 'Training'},
  [NexusNodeType.ItemAssignment]: {icon: IconPackage, color: 'teal', label: 'Item'},
  [NexusNodeType.Schedule]: {icon: IconCalendar, color: 'yellow', label: 'Schedule'},
  [NexusNodeType.BadgeExtension]: {icon: IconId, color: 'pink', label: 'Badge'},
  [NexusNodeType.Unknown]: {icon: IconQuestionMark, color: 'gray', label: 'Unknown'},
};

// --- Phase config (phases exist in timelines but not in the graph) ---

export const phaseConfig: WorkflowEntityConfig = {
  icon: IconArrowsRight,
  color: 'indigo',
  label: 'Phase',
};

// --- Visual state ---

export type VisualState = 'Active' | 'Done' | 'Muted' | 'Cancelled';

export function classifyWorkflowState(state: WorkflowStateEnum): VisualState {
  switch (state) {
    case WorkflowStateEnum.Active:
    case WorkflowStateEnum.Waiting:
      return 'Active';
    case WorkflowStateEnum.Completed:
      return 'Done';
    case WorkflowStateEnum.Cancelled:
      return 'Cancelled';
    case WorkflowStateEnum.Pending:
    case WorkflowStateEnum.Invalid:
    default:
      return 'Muted';
  }
}

export function classifyActionState(state: WorkflowActionStateEnum): VisualState {
  switch (state) {
    case WorkflowActionStateEnum.Waiting:
      return 'Active';
    case WorkflowActionStateEnum.Completed:
      return 'Done';
    case WorkflowActionStateEnum.Cancelled:
      return 'Cancelled';
    case WorkflowActionStateEnum.Pending:
    case WorkflowActionStateEnum.Skipped:
    case WorkflowActionStateEnum.Invalid:
    default:
      return 'Muted';
  }
}

/**
 * Returns the Mantine color for a visual state + entity accent color.
 * - Active → entity type's accent color
 * - Done → green
 * - Muted → gray
 */
export function getVisualStateColor(visualState: VisualState, accentColor: string): string {
  switch (visualState) {
    case 'Active':
      return accentColor;
    case 'Done':
      return 'green';
    case 'Cancelled':
      return 'orange';
    case 'Muted':
      return 'gray';
  }
}

/**
 * Returns the pure state-based Mantine color (no entity accent).
 * Mirrors the nexus graph's 3-color system: blue=active, green=done, gray=muted.
 */
export function getTimelineStateColor(visualState: VisualState): string {
  switch (visualState) {
    case 'Active':
      return 'blue';
    case 'Done':
      return 'green';
    case 'Cancelled':
      return 'orange';
    case 'Muted':
      return 'gray';
  }
}

/**
 * Convenience: get badge color from a workflow state string + accent color.
 * Accepts the enum name string (e.g. "Active", "Completed") for backwards compat.
 */
export function getStateBadgeColor(state: string, accentColor: string): string {
  switch (state.toLowerCase()) {
    case 'active':
    case 'waiting':
      return accentColor;
    case 'completed':
      return 'green';
    case 'cancelled':
      return 'red';
    case 'pending':
    case 'invalid':
    default:
      return 'gray';
  }
}

// --- Date parsing ---

/** Parse a date value that may be a UTC string without 'Z' suffix (from API) or an actual Date. */
export function parseUtcDate(date: Date | string): Date {
  if (date instanceof Date) return date;
  const s = String(date);
  return new Date(s.endsWith('Z') ? s : s + 'Z');
}

// --- Duration formatting ---

export function formatDuration(startDate?: Date | null, endDate?: Date | null): string | null {
  if (!startDate) return null;
  const start = parseUtcDate(startDate);
  const end = endDate ? parseUtcDate(endDate) : new Date();
  const diffMs = end.getTime() - start.getTime();
  const hours = diffMs / (1000 * 60 * 60);

  if (hours < 1) return `${Math.round(hours * 60)} min`;
  if (hours < 24) return `${Math.round(hours)} hours`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? '' : 's'}`;
}

export function formatWaitDurationHours(hours: number | undefined): string {
  if (!hours) return '';
  if (hours < 1) return `${Math.round(hours * 60)} min`;
  if (hours < 24) return `${Math.round(hours)} hours`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? '' : 's'}`;
}

// --- Trigger/action entity type mapping ---

export function getTriggerEntityType(trigger: WorkflowTriggerResponse): NexusNodeType {
  switch (trigger.triggerDefinition?.type) {
    case WorkflowDefinitionTriggerTypeEnum.FormSubmission:
    case WorkflowDefinitionTriggerTypeEnum.FormFieldValueSubmission:
      return NexusNodeType.Form;
    case WorkflowDefinitionTriggerTypeEnum.TaskCompletion:
      return NexusNodeType.Task;
    case WorkflowDefinitionTriggerTypeEnum.Schedule:
      return NexusNodeType.Schedule;
    case WorkflowDefinitionTriggerTypeEnum.Training:
      return NexusNodeType.TrainingSession;
    case WorkflowDefinitionTriggerTypeEnum.ItemTracker:
      return NexusNodeType.ItemAssignment;
    case WorkflowDefinitionTriggerTypeEnum.Manual:
    default:
      return NexusNodeType.Unknown;
  }
}

export function getActionEntityType(type?: WorkflowDefinitionActionTypeEnum): NexusNodeType {
  switch (type) {
    case WorkflowDefinitionActionTypeEnum.CreateTask:
    case WorkflowDefinitionActionTypeEnum.AssignTask:
      return NexusNodeType.Task;
    case WorkflowDefinitionActionTypeEnum.Email:
      return NexusNodeType.Email;
    case WorkflowDefinitionActionTypeEnum.ExtendBadgeExpiration:
      return NexusNodeType.BadgeExtension;
    default:
      return NexusNodeType.Unknown;
  }
}
